import type {
  AllStatuses,
  PreGameStatuses,
  StatusColor
} from '@library/types/status';
import { DateTime } from 'luxon';
import type { Status } from '@library/types/projection';
import { isTimeAfter } from '@library/utils/dateTime';

export const isGameActive = (status: Status) =>
  ['pre_game', 'in_progress'].includes(status);

export const isStatusFinished = (status?: Status) => status === 'finished';

export const isStatusPreGame = (status?: Status) => status === 'pre_game';

export const isStatusInProgress = (status?: Status) => status === 'in_progress';

export const isStatusRemoved = (status?: Status) => status === 'removed';

export const isStatusSuspended = (status?: Status) => status === 'suspended';

export const isStatusTradeable = (status: Status) =>
  ['pre_game', 'removed', 'suspended'].includes(status);

export const getBoardStatus = (statusSet: Set<Status>): Status => {
  if (statusSet.has('pre_game') || !statusSet.size) {
    return 'pre_game';
  }

  if (statusSet.has('in_progress')) {
    return 'in_progress';
  }

  if (statusSet.has('finished')) {
    return 'finished';
  }

  if (statusSet.has('suspended')) {
    return 'suspended';
  }

  return 'removed';
};

export const pregameStatusLive = (
  boardTime?: string,
  status?: AllStatuses
): PreGameStatuses | undefined => {
  if (boardTime && status === 'pre_game') {
    return isTimeAfter({ dateValue: DateTime.fromISO(boardTime) })
      ? 'pre_game not live'
      : 'pre_game live';
  }

  return undefined;
};

export const getColorForStatus = (status?: AllStatuses): StatusColor => {
  switch (status) {
    case 'pre_game not live':
      return { borderColor: 'border-soClean-160', bgColor: 'bg-soClean-160' };
    case 'pre_game live':
    case 'pre_game':
    case 'SIMULATION_COMPLETED':
    case 'PUBLICATION_COMPLETED':
    case 'active':
      return { borderColor: 'border-atlien-200', bgColor: 'bg-atlien-200' };
    case 'in_progress':
      return { borderColor: 'border-msJackson', bgColor: 'bg-msJackson' };
    case 'suspended':
    case 'PUP-P':
    case 'NFI-A':
    case 'QUESTIONABLE':
    case 'PUBLICATION_IN_PROGRESS':
    case 'SIMULATION_IN_PROGRESS':
      return { borderColor: 'border-spottieOttie', bgColor: 'bg-spottieOttie' };
    case 'prod':
    case 'removed':
    case 'IR':
    case 'PUP-R':
    case 'NFI-R':
    case 'RESERVE-RET':
    case 'RESERVE-SUS':
    case 'PUBLICATION_FAILED':
    case 'OUT':
    case 'SIMULATION_FAILED':
    case 'deactivated':
      return { borderColor: 'border-roses', bgColor: 'bg-roses' };
    case 'finished':
      return {
        borderColor: 'border-stankonia-100',
        bgColor: 'bg-stankonia-100'
      };
    default:
      return { borderColor: 'border-soClean-160', bgColor: 'bg-soClean-160' };
  }
};

export const getTooltipForStatus = (status?: AllStatuses): string => {
  switch (status) {
    case 'pre_game live':
      return 'Pre Game, On the Board';
    case 'pre_game not live':
      return 'Pre Game, Off the Board';
    case 'pre_game':
      return 'Pre Game';
    case 'active':
      return 'Active';
    case 'in_progress':
      return 'In Progress';
    case 'prod':
      return 'Prod';
    case 'deactivated':
      return 'Deactivated';
    case 'suspended':
      return 'Suspended';
    case 'removed':
      return 'Removed';
    case 'finished':
      return 'Finished';
    case 'QUESTIONABLE':
      return 'Questionable';
    case 'PUP-P':
      return 'PUP-P';
    case 'PUP-R':
      return 'PUP-R';
    case 'NFI-A':
      return 'NFI-A';
    case 'OUT':
      return 'OUT';
    case 'NFI-R':
      return 'NFI-R';
    case 'IR':
      return 'IR';
    case 'RESERVE-RET':
      return 'RESERVE-RET';
    case 'RESERVE-SUS':
      return 'RESERVE-SUS';
    case 'PUBLICATION_COMPLETED':
      return 'Publication Completed';
    case 'PUBLICATION_IN_PROGRESS':
      return 'Publication In Progress';
    case 'PUBLICATION_FAILED':
      return 'Publication Failed';
    case 'SIMULATION_COMPLETED':
      return 'Simulation Completed';
    case 'SIMULATION_IN_PROGRESS':
      return 'Simulation In Progress';
    case 'SIMULATION_FAILED':
      return 'Simulation Failed';
    default:
      return '';
  }
};
